import type { IPredictionWithSensor, ISensor } from "@elproag/predict";
import type { ColDef, GridOptions } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { Button, Skeleton, Spin } from "antd";
import dayjs from "dayjs";
import React, {
	type ReactNode,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom-v5-compat";
import { Paths } from "../../../common/constants";
import { AuthContext } from "../../../common/context";
import { PredictionService } from "../../../common/services";
import { Card } from "../../Common";
import { ErrorAlert } from "../../Common/Alerts";
import { formatDayJsInUserTz } from "../../Infrastructure/DateTime/DateTimeUtils";
import CountStatusBar from "../../SensorListView/CountStatusBar";
import { AgGridWrapper } from "../../Shared/Components";

function SensorAnalysisCellRenderer(
	props: Readonly<{ value: ISensor }>,
): ReactNode {
	if (!props.value) return "Unknown or deleted sensor";
	return (
		<Link to={`${Paths.Sensor.Analysis}/${props.value.id}`}>
			{props.value.name}
		</Link>
	);
}

export function PredictionGrid() {
	const navigate = useNavigate();
	const gridRef = useRef<AgGridReact>(null);
	const [predictions, setPredictions] = useState<IPredictionWithSensor[]>([]);
	const authContext = useContext(AuthContext);

	const [loading, setLoading] = useState(true);

	const orgUuid = authContext.Organization?.Uuid;

	useEffect(() => {
		if (orgUuid) {
			PredictionService.GetPredictions(orgUuid)
				.then((predictions) =>
					predictions.sort(
						(a, b) =>
							b.prediction.createdAtUnixUtc - a.prediction.createdAtUnixUtc,
					),
				)
				.then(setPredictions)
				.catch((_) => ErrorAlert.fire({ text: "Failed to load predictions" }))
				.finally(() => setLoading(false));
		}
	}, [orgUuid]);

	const defaultTextFilter = {
		filter: "agTextColumnFilter",
		filterParams: {
			filterOptions: ["contains"],
			maxNumConditions: 1,
		},
	};

	const colDefs: ColDef<IPredictionWithSensor>[] = useMemo(
		() => [
			{
				headerName: "Sensor Name",
				valueGetter: (row) => row.data.sensor,
				cellRenderer: SensorAnalysisCellRenderer,
				flex: 1,
				resizable: true,
				...defaultTextFilter,
			},
			{
				headerName: "Serial Number",
				field: "sensor.serialNumber",
				flex: 1,
				resizable: true,
				...defaultTextFilter,
			},
			{
				valueGetter: (params) =>
					dayjs.unix(params.data.prediction?.createdAtUnixUtc),
				valueFormatter: (params) => formatDayJsInUserTz(params.value),
				headerName: "Started at",
				filter: "agDateColumnFilter",
				filterParams: {
					buttons: ["reset"],
				},
				flex: 1,
			},
		],
		[],
	);

	const statusBar: GridOptions["statusBar"] = {
		statusPanels: [{ statusPanel: CountStatusBar, align: "left" }],
	};

	return (
		<Card
			title="Predictions"
			styles={{ body: { height: "95%" } }}
			style={{ height: "100%" }}
			extra={
				<Button
					onClick={() => navigate(Paths.ElproPredict.Configure)}
					type={"primary"}
				>
					Configure Sensors
				</Button>
			}
		>
			{loading ? (
				<Skeleton />
			) : (
				<AgGridWrapper>
					<AgGridReact<IPredictionWithSensor>
						rowData={predictions}
						columnDefs={colDefs}
						statusBar={statusBar}
						ref={gridRef}
						loadingOverlayComponent={Spin}
					></AgGridReact>
				</AgGridWrapper>
			)}
		</Card>
	);
}
